// warehouse/connection.js — apertura, cifrado y ciclo de vida físico de la
// bodega local (F6.4a). Réplica del patrón validado en el spike F6.1
// (f4_gate/www/vault.js): SQLCipher real on-device con llave EXTERNA de 32
// bytes cuya FUENTE DE VERDAD es el Keychain de la app, WAL, y verificación
// cruzada de la llave en cada apertura.
//
// Plataformas, dicho honesto:
//   nativa (iOS): archivo SQLCipher real en Library/CapacitorDatabase (fuera
//     del WebView, sin backup a iCloud — config del plugin en
//     capacitor.config.json), llave en Keychain.
//   web: la bodega NO existe todavía y la web JAMÁS intenta cifrar (el plugin
//     no tiene cifrado web). `encrypted` se calcula como "plataforma nativa"
//     — cualquier apertura futura en web sería sin cifrado; hoy, sin el
//     custom element del plugin registrado, abrir en web falla contenido y
//     los llamadores (identity.js) degradan a no-op.
//
// ⚠️ Trampa del proxy de Capacitor (el hang de boot de F4.2c, docstring en
// tokenStore.js): un proxy de registerPlugin convierte CUALQUIER acceso a
// propiedad — incluido `then` — en llamada nativa. Los proxies aquí viven
// SIEMPRE dentro de objetos planos ({ sqlite } / { plugin }) construidos sin
// que el proxy sea jamás el valor de resolución de una promesa; solo se
// await-ean sus LLAMADAS a métodos.

import {
  SCHEMA_STATEMENTS,
  PURGE_ALL_ROWS_STATEMENTS,
  DROP_ALL_TABLES_STATEMENTS,
  CREATE_SYNC_META,
  SYNC_META_KEYS,
  WAREHOUSE_SCHEMA_VERSION,
} from './schema';

// Nombre del archivo .db (el plugin agrega el sufijo SQLite.db).
export const WAREHOUSE_DB_NAME = 'studybuddy_warehouse';
const WAREHOUSE_DB_VERSION = 1;
// Llave de cifrado: 32 bytes aleatorios, serializados a hex.
const DB_KEY_BYTES = 32;
// Entrada del Keychain (vía @aparajita/capacitor-secure-storage, mismo wrapper
// que tokenStore.js usa para los tokens) donde vive la fuente de verdad de la
// llave. Constante única — nunca esparcir el literal.
const WAREHOUSE_KEY_NAME = 'sb_warehouse_db_key';
// Techo duro por operación de plugin nativo (mismo racional que
// NATIVE_KEYCHAIN_TIMEOUT_MS en tokenStore.js): si el bridge no contesta, la
// operación DEGRADA en vez de colgar un flujo de auth para siempre.
const NATIVE_PLUGIN_TIMEOUT_MS = 5000;

export function isNativePlatform() {
  return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
}

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      setTimeout(
        () => reject(new Error(`${label} timed out after ${NATIVE_PLUGIN_TIMEOUT_MS}ms`)),
        NATIVE_PLUGIN_TIMEOUT_MS
      );
    }),
  ]);
}

// SYNC a propósito (trampa del proxy): devuelve los proxies dentro de un
// objeto plano; nunca los resuelvas desde una función async.
function sqlitePluginSync() {
  const P = typeof window !== 'undefined' ? window.Capacitor?.Plugins : undefined;
  if (!P?.CapacitorSQLite) {
    throw new Error('CapacitorSQLite no está registrado (¿plataforma web o falta cap sync?)');
  }
  return { sqlite: P.CapacitorSQLite };
}

// Mismo patrón que tokenStore.getSecureStorage: import dinámico (chunk lazy
// que el bundle web nunca pide) y wrapper { plugin } — NUNCA el proxy pelado
// como valor de resolución.
async function getSecureStorage() {
  const mod = await import('@aparajita/capacitor-secure-storage');
  return { plugin: mod.SecureStorage };
}

function randomKeyHex() {
  const bytes = new Uint8Array(DB_KEY_BYTES);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Quirk iOS del API raw del plugin (hallazgo F6.1, vault.js): las queries
// directas al proxy anteponen una fila marcador {"ios_columns": [...]}. El
// wrapper JS del plugin la quita; como llamamos al proxy directo, la quitamos
// aquí. Android/web devuelven filas planas y pasan intactos.
export function normalizeIosRows(rows) {
  if (rows.length && rows[0] !== null && typeof rows[0] === 'object' && 'ios_columns' in rows[0]) {
    return rows.slice(1);
  }
  return rows;
}

// La llave de 32 bytes: si no existe en el Keychain se genera UNA vez y se
// guarda; si ya existe se reutiliza — NUNCA se regenera sobre una existente.
async function getOrCreateDbKey() {
  const { plugin } = await getSecureStorage();
  const existing = await withTimeout(plugin.getItem(WAREHOUSE_KEY_NAME), 'Keychain getItem (db key)');
  if (typeof existing === 'string' && existing.length > 0) {
    return existing;
  }
  const keyHex = randomKeyHex();
  await withTimeout(plugin.setItem(WAREHOUSE_KEY_NAME, keyHex), 'Keychain setItem (db key)');
  return keyHex;
}

// Cross-validación de las DOS copias de la llave (Keychain nuestro ↔ keychain
// del plugin) en cada apertura. Estados posibles y su resolución:
//   - plugin sin secret → setEncryptionSecret(llave) (primera vez, o el
//     purge quedó a medias entre clearEncryptionSecret y removeItem — el
//     orden del purge garantiza que este estado es re-sincronizable).
//   - secret presente y coincide → OK.
//   - secret presente y NO coincide → error CONTENIDO (identity.js lo traga:
//     bodega no disponible este run). No hay self-heal posible: el archivo
//     quedó cifrado con una llave perdida y el plugin se niega a borrar un
//     .db cifrado sin poder abrirlo (CapacitorSQLite.swift, deleteDatabase →
//     open()); borrarlo a mano exigiría @capacitor/filesystem, que este repo
//     no incluye. El estado es INALCANZABLE por construcción: ambas copias se
//     escriben juntas, se borran juntas (plugin primero, Keychain después), y
//     toda ventana de kill intermedia converge a un estado re-sincronizable
//     (ver purgeWarehouseStorage). Si algún día aparece, preferimos el fallo
//     ruidoso y localizable a nuclear datos en silencio.
async function syncEncryptionSecret(sqlite, keyHex) {
  const stored = await withTimeout(sqlite.isSecretStored(), 'isSecretStored');
  if (!stored.result) {
    await withTimeout(sqlite.setEncryptionSecret({ passphrase: keyHex }), 'setEncryptionSecret');
    return;
  }
  const check = await withTimeout(sqlite.checkEncryptionSecret({ passphrase: keyHex }), 'checkEncryptionSecret');
  if (!check.result) {
    throw new Error('la llave del Keychain NO coincide con el secret del plugin — bodega no disponible');
  }
}

// Estado memoizado de la conexión. `opening` deduplica aperturas concurrentes
// (misma razón que el dedupe de inflight en screenDataCache).
const warehouse = { api: null, opening: null };

async function openWarehouseInner() {
  // encrypted = "no es web": la web JAMÁS intenta cifrar (no existe soporte).
  const encrypted = isNativePlatform();
  const { sqlite } = sqlitePluginSync();

  if (encrypted) {
    const keyHex = await getOrCreateDbKey();
    await syncEncryptionSecret(sqlite, keyHex);
  }

  // Limpia una entrada huérfana del registro del plugin (p. ej. un open()
  // anterior que falló después de createConnection): sin entrada es un return
  // silencioso; con entrada la cierra — así createConnection nunca choca con
  // "connection already exists".
  await withTimeout(sqlite.closeConnection({ database: WAREHOUSE_DB_NAME }), 'closeConnection (pre-open)');

  await withTimeout(
    sqlite.createConnection({
      database: WAREHOUSE_DB_NAME,
      version: WAREHOUSE_DB_VERSION,
      encrypted,
      mode: encrypted ? 'secret' : 'no-encryption',
      readonly: false,
    }),
    'createConnection'
  );
  await withTimeout(sqlite.open({ database: WAREHOUSE_DB_NAME }), 'open');

  // Objeto PLANO (sin `then`) — seguro como valor de resolución de promesas.
  const api = {
    async execute(statements, transaction = false) {
      return sqlite.execute({ database: WAREHOUSE_DB_NAME, statements, transaction });
    },
    async run(statement, values = [], transaction = false) {
      return sqlite.run({ database: WAREHOUSE_DB_NAME, statement, values, transaction });
    },
    async query(statement, values = []) {
      const r = await sqlite.query({ database: WAREHOUSE_DB_NAME, statement, values });
      return normalizeIosRows(r.values ?? []);
    },
    // Lote de statements PARAMETRIZADOS en una transacción del plugin (el
    // downloader de F6.4b escribe sus fotos/bundles con esto: valores fuera
    // del SQL, todo-o-nada por llamada). set = [{ statement, values }].
    async executeSet(set, transaction = true) {
      return sqlite.executeSet({ database: WAREHOUSE_DB_NAME, set, transaction });
    },
  };

  // WAL fuera de cualquier transacción (journal_mode no cambia dentro de una).
  // Es la base del todo-o-nada del purge (torture test F6.1: 4,964 tx).
  await api.execute('PRAGMA journal_mode=WAL;', false);
  await applySchemaWithMigration(api);

  warehouse.api = api;
  return api;
}

// ── Migración (F6.4b recreación → F6.5a escalera ALTER) ─────────────────────
//
// Desde v3 existe el diario local (local_events): renglones sin subir =
// IRREMPLAZABLES. Por eso hay dos regímenes, con la frontera en v3:
//   - guardada < v3 (o ausente): la bodega es cache puro re-descargable →
//     migración-por-recreación (v2→v3 es la ÚLTIMA legal; de paso resuelve
//     sola el backfill de documents.chunk_index: bodega fresca → re-descarga
//     con la columna).
//   - guardada >= 3 y ≠ vigente: ESCALERA de pasos ALTER (stored → stored+1 →
//     … → vigente). Cada paso es transaccional (executeSet), sync_meta se
//     preserva COMPLETO, y un salto sin paso registrado — incluida una bodega
//     de un build MÁS NUEVO — es un fallo contenido y ruidoso ("bodega
//     requiere migración desconocida"), JAMÁS un DROP silencioso.

// La frontera del régimen: v3 introduce el diario. No mover jamás hacia
// arriba — subirla convertiría bodegas con renglones en candidatas a DROP.
const LAST_RECREATE_VERSION = 3;

// Registro de pasos ALTER: { [desdeVersión]: () => [{ statement, values }] }.
// El paso N migra vN → vN+1 y DEBE incluir todos sus CREATE/ALTER (la
// escalera no re-aplica SCHEMA_STATEMENTS: cada paso es dueño de su cambio).
export const MIGRATION_STEPS = {
  // v3 → v4 (F6.6a, D-O): saved_positions gana la LÁPIDA `cleared`. El
  // PORQUÉ: un clear-position hecho offline debe SOBREVIVIR en la bodega
  // hasta subirse al server — si el clear fuera un DELETE físico, la
  // siguiente foto de /sync/state (que aún trae la posición del server)
  // la resucitaría y el usuario vería revivir la posición que ya borró.
  // La lápida (cleared=1) persiste el intento de borrado, viaja en el carril
  // positions[] del sobre, y solo se borra físico cuando el ack confirma
  // que el server ya lo sabe. ALTER (no recreación): desde v3 el diario
  // local hace la bodega irremplazable — contrato de connection.js.
  3: () => [{
    statement: 'ALTER TABLE saved_positions ADD COLUMN cleared INTEGER NOT NULL DEFAULT 0;',
    values: [],
  }],

  // v4 → v5 (F6.6b Tanda 2a): el EXAMEN local. Dos tablas, cero recreación.
  //
  //   local_sessions — la sesión EN VUELO deja de ser solo-adaptive. El
  //     examen necesita persistir su paquete servido (question_ids +
  //     questions_snapshot: la clave de calificación, NO reconstruible desde
  //     el diario) y su estado de avance (answers_so_far / question_times /
  //     current_index), más el reloj de D-N (deadline_at, backgrounded_at,
  //     time_remaining_seconds) y el resultado que el espejo de G1 repinta
  //     sin re-calificar (score + results_summary_json; correct_count ya
  //     existía). `selection_seed` es forense del sorteo local.
  //
  //   exam_sessions — la FOTO del server gana el kit de reanudación que
  //     /sync/state ya baja para las pausadas (D-L, Tanda 1). Sin estas
  //     columnas la pausa aterriza sin nada que continuar.
  //
  // TODAS nullables y sin DEFAULT: un ALTER ADD COLUMN nullable no reescribe
  // filas en SQLite, así que el paso no puede perder un renglón del diario.
  4: () => [
    // local_sessions — el examen en vuelo
    'ALTER TABLE local_sessions ADD COLUMN question_ids_json TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN questions_snapshot_json TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN answers_so_far_json TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN question_times_json TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN current_index INTEGER;',
    'ALTER TABLE local_sessions ADD COLUMN time_limit INTEGER;',
    'ALTER TABLE local_sessions ADD COLUMN total_questions INTEGER;',
    'ALTER TABLE local_sessions ADD COLUMN score REAL;',
    'ALTER TABLE local_sessions ADD COLUMN results_summary_json TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN selection_seed INTEGER;',
    'ALTER TABLE local_sessions ADD COLUMN deadline_at TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN time_remaining_seconds REAL;',
    'ALTER TABLE local_sessions ADD COLUMN paused_at TEXT;',
    'ALTER TABLE local_sessions ADD COLUMN backgrounded_at TEXT;',
    // exam_sessions — el kit de reanudación de la foto
    'ALTER TABLE exam_sessions ADD COLUMN answers_so_far_json TEXT;',
    'ALTER TABLE exam_sessions ADD COLUMN question_times_json TEXT;',
    'ALTER TABLE exam_sessions ADD COLUMN current_index INTEGER;',
    'ALTER TABLE exam_sessions ADD COLUMN time_remaining_seconds REAL;',
    'ALTER TABLE exam_sessions ADD COLUMN rng_seeds_json TEXT;',
  ].map((statement) => ({ statement, values: [] })),

  // v5 → v6 (RPW Tanda 3): la FOTO gana la HORA LÍMITE del examen que otro
  // dispositivo tiene en vuelo.
  //
  // EL PORQUÉ: el kit de reanudación de /sync/state cambió de clientela — de
  // las sesiones `paused` (que ya nadie puede crear) a las `in_progress` con
  // `deadline_at`. Y con la clientela cambió el reloj: una pausa bajaba con su
  // restante CONGELADO (`time_remaining_seconds`), un examen en vuelo baja con
  // su HORA LÍMITE, que es la única verdad de su tiempo. Sin esta columna la
  // hora llega y se tira, y el examen ajeno no se puede adoptar.
  //
  // Nullable y sin DEFAULT, igual que todo el paso anterior: un ALTER ADD
  // COLUMN nullable no reescribe filas en SQLite, así que no puede perder un
  // renglón del diario. Las filas que ya estaban en `exam_sessions` la leen
  // como NULL — y una fila sin hora límite es exactamente lo que el resolutor
  // ignora.
  5: () => [{
    statement: 'ALTER TABLE exam_sessions ADD COLUMN deadline_at TEXT;',
    values: [],
  }],

  // v6 → v7 (RPW Tanda 4, D-4c): la sesión local gana su MARCADOR DE ORIGEN.
  //
  // EL PORQUÉ: desde la T3 el cel puede ADOPTAR un examen que nació en la web y
  // sigue vivo en el servidor. Ese examen tiene DOS corredores, y por eso —y
  // solo él— respalda al servidor lo que se contesta aquí y acepta que la foto
  // rellene al entrar. El examen NACIDO en el cel es 100% local hasta su
  // entrega, y confundir los dos lo haría respaldar una sesión que el servidor
  // no conoce. `origin` es el único interruptor de esa distinción.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — que es
  // exactamente "nació aquí", el comportamiento de siempre.
  6: () => [{
    statement: 'ALTER TABLE local_sessions ADD COLUMN origin TEXT;',
    values: [],
  }],

  // v7 → v8 (F6.8 Tanda C): la sesión local gana su ACUSE POR ACTA.
  //
  // EL PORQUÉ: hasta v7, lo único que el cartero podía dar por entregado era un
  // RENGLÓN (`local_events.upload_state`). Un acta que viaja SOLA no tiene
  // renglón donde anotarse — y hay dos que viajan solas por construcción: el
  // examen entregado SIN contestar nada (INC-3: cero respuestas = cero eventos)
  // y el cierre de una adaptive cuyos renglones ya subieron (mecanismo B). Sin
  // esta marca, un acta huérfana re-viajaría en cada ciclo para siempre; con
  // ella viaja UNA vez y se sella cuando el servidor la confirma.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — "sin
  // acuse todavía", que es exactamente la verdad de todas ellas.
  7: () => [{
    statement: 'ALTER TABLE local_sessions ADD COLUMN acta_acked_at TEXT;',
    values: [],
  }],

  // v8 → v9 (F6.8 Tanda 6, T6-2): la sesión local gana el INSTANTE DE SU SELLO
  // DEL RELOJ.
  //
  // EL PORQUÉ: `time_remaining_seconds` dice CUÁNTO quedaba y no CUÁNDO se midió,
  // y sin el cuándo ese número no se puede confrontar con nada. Juntos son un par
  // {restante, instante} que ataja el RETROCESO del reloj: si al reanudar la hora
  // del dispositivo marca ANTES que el último sello, el restante sellado manda
  // como techo — atrasar el reloj deja de regalar tiempo de examen. El ancla
  // monótona (T6-1) cubre el proceso vivo; esta columna cubre lo que el ancla no
  // puede, que es el arranque tras un kill.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — "este
  // restante no trae instante", y contra eso el piso NO dispara: pasa intacto,
  // que es exactamente el comportamiento de siempre.
  8: () => [{
    statement: 'ALTER TABLE local_sessions ADD COLUMN clock_stamped_at TEXT;',
    values: [],
  }],

  // v9 → v10 (F6.8 S-8, S8-F1-b): la sesión local gana su FECHA HONESTA.
  //
  // EL PORQUÉ: el piso de la v9 no sólo recorta el restante — también RE-ANCLA la
  // hora límite, y la expresa contra el `now` de la línea de tiempo ATRASADA. Esa
  // hora límite queda viviendo allá. En cuanto el usuario DESHACE su retroceso,
  // la resta se hace entre dos líneas de tiempo distintas: el `now` restaurado es
  // posterior a esa hora límite y las tres puertas que declaran vencido lo
  // entregan — incluso sin volver a entrar al examen, con sólo abrir el Dashboard
  // o Analytics. Esta columna guarda la hora límite PREVIA al primer re-ancla, y
  // con ella la sesión puede sanar de vuelta a su línea honesta.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — "a esta
  // sesión nunca le movieron la hora límite", y contra NULL el rescate NO
  // dispara: el comportamiento de siempre, intacto.
  9: () => [{
    statement: 'ALTER TABLE local_sessions ADD COLUMN deadline_original_at TEXT;',
    values: [],
  }],

  // v10 → v11 (F6.8 sprint c): la sesión local gana el ACUSE DE SU APERTURA.
  //
  // EL PORQUÉ: hasta v10 lo único que el cel podía dar por entregado era un
  // RENGLÓN (`local_events.upload_state`) o un acta TERMINADA (`acta_acked_at`,
  // v8). La APERTURA de un examen —el hecho de que empezó, que es lo que esta
  // tanda hace viajar— no es ninguna de las dos: la sesión sigue EN VUELO y no
  // produce ni un renglón hasta la entrega (censo C-C5). Sin una marca propia,
  // la apertura re-viajaría en cada ciclo para siempre; con ella viaja hasta que
  // el servidor la confirma y se calla.
  //
  // Es una columna APARTE y no un re-uso de `acta_acked_at` porque los dos
  // carriles conviven sobre la MISMA fila: sellar la apertura en la columna del
  // acta sacaría a esa sesión del carril de huérfanas antes de que cierre, y su
  // entrega real se quedaría sin vehículo — justo el agujero que la Tanda C
  // vino a tapar.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — "esta
  // apertura todavía no la confirma nadie", que es exactamente la verdad de
  // todas ellas.
  10: () => [{
    statement: 'ALTER TABLE local_sessions ADD COLUMN apertura_acked_at TEXT;',
    values: [],
  }],

  // v11 → v12 (TANDA M, M1 + M6): el documento gana su CANDADO.
  //
  // EL PORQUÉ: hasta v11 una fila de `documents` sólo tenía dos estados —
  // presente (servible) o ausente (borrada). Una bajada de plan ARCHIVA el
  // excedente en el servidor y `/sync/state` deja de listarlo, así que el
  // aparato recibía exactamente la misma señal que con un borrado y purgaba el
  // material entero. Dos daños firmados salían de ahí: el que vuelve a pagar
  // tiene que re-descargar todo (censo #10), y el estudio hecho sin señal sobre
  // ese documento perdía su acta y ya no podía llegar nunca al servidor (censo
  // #1). Esta columna es el tercer estado que faltaba: conservado y bloqueado.
  //
  // Es del DOCUMENTO y no del pool a propósito: el bloqueo lo decide el plan,
  // no el material. Y es una marca aparte de `status` porque `status` guarda lo
  // que el servidor dice del pool (`ready`/`partial`) y hay que poder
  // devolverlo tal cual al desbloquear.
  //
  // Nullable y sin DEFAULT, como todos los pasos desde v4: un ALTER ADD COLUMN
  // nullable no reescribe filas en SQLite, así que el paso no puede perder un
  // renglón del diario. Las filas que ya existían la leen como NULL — "este
  // documento no está bloqueado", que es la verdad de todas ellas.
  11: () => [{
    statement: 'ALTER TABLE documents ADD COLUMN locked_at TEXT;',
    values: [],
  }],

  // v12 → v13 (TANDA 32, #299): LOS CUATRO CARRILES APRENDEN A RENDIRSE.
  //
  // EL PORQUÉ: el candado del logout (`dispatcher.js::countUnsentWork`) cuenta
  // trabajo "sin subir", y hasta v12 ninguna de las cuatro fuentes tenía manera
  // de dejar de estarlo cuando el servidor no la acepta. Un renglón rechazado
  // como TRANSITORIO se queda en `pending` para siempre —no hay techo de
  // intentos, no hay caducidad—, y como eso vive en SQLite, sobrevive a matar la
  // app: el aviso "hay cosas que aún no se sincronizan" volvía idéntico en cada
  // reapertura y no había gesto del usuario que lo pudiera resolver. Es el
  // atorón que Carlos cazó en el iPhone con Wi-Fi.
  //
  // Y NO ES UN CASO RARO: la clasificación de rechazos tiene un default
  // conservador (`REJECT_REASON_CLASS[…] || 'transient'`), así que CUALQUIER
  // razón que el servidor invente y esta versión de la app no conozca produce
  // un bloqueo permanente por diseño.
  //
  // TRES COLUMNAS, UN SOLO CONCEPTO: cuántas veces esta fila viajó y volvió sin
  // ser aceptada. Al llegar al techo (`MAX_SYNC_ATTEMPTS`) la fila deja de
  // contar como pendiente — y en el caso de los renglones, además cambia a
  // `rejected_final` con su razón, que es el estado que ya existía para "el
  // servidor no va a tomar esto".
  //
  //   local_events.reject_attempts  — carril 1 (renglones de estudio)
  //   local_sessions.sync_attempts  — carriles 2 y 3 (actas huérfanas y
  //     aperturas de examen). UNA columna sirve a los dos porque son
  //     EXCLUYENTES: el carril del acta pide `completed = 1` y el de la
  //     apertura `completed = 0`, así que una fila jamás está en ambos.
  //   saved_positions.sync_attempts — carril 4 (posiciones de flashcards)
  //
  // NADA SE BORRA NUNCA POR ESTO. Llegar al techo sólo significa "deja de
  // bloquear el logout"; la fila se queda entera en la bodega con su cuenta a la
  // vista, y en el caso de las posiciones sigue siendo el cache que la pantalla
  // lee. Rendirse es dejar de PROMETER que va a subir, no tirar el contenido.
  //
  // ALTER con DEFAULT 0 y NOT NULL: SQLite lo resuelve sin reescribir filas y
  // toda la población existente arranca en cero, o sea con sus intentos
  // completos por delante. Una bodega atorada HOY se destraba sola en cuanto
  // gasta su techo, sin que el usuario tenga que hacer nada.
  12: () => [
    'ALTER TABLE local_events ADD COLUMN reject_attempts INTEGER NOT NULL DEFAULT 0;',
    'ALTER TABLE local_sessions ADD COLUMN sync_attempts INTEGER NOT NULL DEFAULT 0;',
    'ALTER TABLE saved_positions ADD COLUMN sync_attempts INTEGER NOT NULL DEFAULT 0;',
  ].map((statement) => ({ statement, values: [] })),
};

// Sube la bodega por la escalera, un paso transaccional a la vez. La versión
// se registra DENTRO del mismo executeSet del paso: o el paso completo entró
// (cambios + versión nueva) o no entró nada — un kill a media migración
// reintenta el mismo paso al reabrir.
async function runMigrationLadder(api, storedVersion, targetVersion, steps) {
  if (storedVersion > targetVersion) {
    // Bodega de un build más nuevo (downgrade de app): sin paso hacia atrás
    // no hay migración posible — fallo ruidoso, el diario queda intacto.
    throw new Error(
      `bodega requiere migración desconocida (v${storedVersion} → v${targetVersion}) — jamás se recrea desde v${LAST_RECREATE_VERSION}`
    );
  }
  for (let v = storedVersion; v < targetVersion; v++) {
    const step = steps[v];
    if (!step) {
      throw new Error(
        `bodega requiere migración desconocida (v${v} → v${v + 1}) — jamás se recrea desde v${LAST_RECREATE_VERSION}`
      );
    }
    const set = step();
    set.push({
      statement: 'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
      values: [SYNC_META_KEYS.SCHEMA_VERSION, String(v + 1)],
    });
    await api.executeSet(set, true);
  }
  // Epoch fresco tras migrar (mismo racional que la recreación): cualquier
  // escritura en vuelo que capturó el epoch pre-migración aborta en su COMMIT.
  await api.run(
    'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
    [SYNC_META_KEYS.WAREHOUSE_EPOCH, String(Date.now())],
    false
  );
}

// Secuencia por apertura:
//   1. sync_meta se garantiza primero (es donde vive la versión).
//   2. Versión guardada == la del código → camino normal: schema idempotente
//      + semilla de epoch si falta. Cero trabajo extra por apertura.
//   3. Versión ausente o < v3 (bodega F6.4a/F6.4b, o RECIÉN creada — recrear
//      tablas vacías es no-op) → recreación: DROP + recrear + registrar la
//      versión. Solo sobrevive owner_user_id (identidad, no metadato de
//      sync); el resto muere con el DROP — la próxima descarga lo repuebla.
//   4. Versión >= 3 y distinta → escalera ALTER (arriba). sync_meta COMPLETO
//      se preserva; la recreación está PROHIBIDA en este régimen.
//   5. El epoch se siembra SIEMPRE fresco tras cualquier migración.
// `steps`/`targetVersion` son inyectables SOLO para tests (el paso simulado
// v3→v4 del detector de reversión); producción usa los defaults.
export async function applySchemaWithMigration(
  api,
  { steps = MIGRATION_STEPS, targetVersion = WAREHOUSE_SCHEMA_VERSION } = {}
) {
  await api.execute(CREATE_SYNC_META, false);
  const rows = await api.query(
    'SELECT value FROM sync_meta WHERE key = ?;',
    [SYNC_META_KEYS.SCHEMA_VERSION]
  );
  const stored = rows.length > 0 ? rows[0].value : null;

  if (stored === String(targetVersion)) {
    await api.execute(SCHEMA_STATEMENTS, false);
    // Semilla del epoch (ver identity.js): solo si no existe. Date.now() como
    // valor inicial garantiza que un epoch re-sembrado tras un purge (que
    // borra el archivo) siempre difiere del que capturó cualquier escritura
    // en vuelo.
    await api.run(
      'INSERT OR IGNORE INTO sync_meta (key, value) VALUES (?, ?);',
      [SYNC_META_KEYS.WAREHOUSE_EPOCH, String(Date.now())],
      false
    );
    return;
  }

  const storedNumber = stored === null ? null : Number(stored);
  if (storedNumber !== null && Number.isFinite(storedNumber)
      && storedNumber >= LAST_RECREATE_VERSION) {
    await runMigrationLadder(api, storedNumber, targetVersion, steps);
    return;
  }

  const ownerRows = await api.query(
    'SELECT value FROM sync_meta WHERE key = ?;',
    [SYNC_META_KEYS.OWNER_USER_ID]
  );
  const owner = ownerRows.length > 0 && ownerRows[0].value ? ownerRows[0].value : null;

  await api.execute(DROP_ALL_TABLES_STATEMENTS, false);
  await api.execute(SCHEMA_STATEMENTS, false);
  await api.run(
    'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
    [SYNC_META_KEYS.SCHEMA_VERSION, String(targetVersion)],
    false
  );
  if (owner) {
    await api.run(
      'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
      [SYNC_META_KEYS.OWNER_USER_ID, owner],
      false
    );
  }
  await api.run(
    'INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?);',
    [SYNC_META_KEYS.WAREHOUSE_EPOCH, String(Date.now())],
    false
  );
}

// Abre (o devuelve) la conexión única a la bodega. En web, sin el plugin
// registrado, rechaza — los llamadores de F6.4a (identity.js) lo contienen.
export function openWarehouse() {
  if (warehouse.api) return Promise.resolve(warehouse.api);
  if (warehouse.opening) return warehouse.opening;
  const p = openWarehouseInner().finally(() => {
    if (warehouse.opening === p) warehouse.opening = null;
  });
  warehouse.opening = p;
  return p;
}

async function closeWarehouse(sqlite) {
  warehouse.api = null;
  try {
    await withTimeout(sqlite.closeConnection({ database: WAREHOUSE_DB_NAME }), 'closeConnection');
  } catch (e) {
    // Sin conexión registrada no hay nada que cerrar — no es un fallo.
  }
}

// Destrucción CONJUNTA de las dos copias de la llave. ORDEN deliberado:
// primero el secret del plugin, después la entrada del Keychain — si la app
// muere en medio, el estado que queda (Keychain con llave + plugin sin
// secret) se re-sincroniza solo en la próxima apertura (setEncryptionSecret).
// El orden inverso dejaría un secret huérfano que ninguna llave nuestra puede
// validar (mismatch permanente). Si borrar el secret del plugin falla, NO se
// toca la copia del Keychain: ambas copias viven juntas o mueren juntas.
async function destroyWarehouseStorageKeys(sqlite) {
  await withTimeout(sqlite.clearEncryptionSecret(), 'clearEncryptionSecret');
  const { plugin } = await getSecureStorage();
  await withTimeout(plugin.removeItem(WAREHOUSE_KEY_NAME), 'Keychain removeItem (db key)');
}

// "Purga TODO" físico (la mitad mecánica de identity.js): filas + archivo +
// ambas copias de la llave. Secuencia y sus ventanas de kill, todas
// convergentes al reabrir:
//   1. DELETE de todas las filas en UNA transacción (todo-o-nada bajo WAL).
//      Kill aquí → o quedó todo o no quedó nada.
//   2. deleteDatabase con la conexión aún registrada (el plugin exige la
//      entrada en su diccionario; cerrarla antes haría fallar el borrado).
//      Kill entre 1 y 2 → archivo vacío con llaves intactas: abre normal.
//   3. closeConnection (limpia el registro del plugin).
//   4. Destrucción conjunta de llaves (ver destroyWarehouseStorageKeys).
//      Kill entre 2/3 y 4 → sin archivo, llaves intactas: abre fresco.
// En web (sin bodega) es no-op.
export async function purgeWarehouseStorage() {
  if (!isNativePlatform()) return;
  const { sqlite } = sqlitePluginSync();
  const api = await openWarehouse();
  await api.execute(PURGE_ALL_ROWS_STATEMENTS, true);
  await withTimeout(sqlite.deleteDatabase({ database: WAREHOUSE_DB_NAME }), 'deleteDatabase');
  await closeWarehouse(sqlite);
  await destroyWarehouseStorageKeys(sqlite);
}

// Solo para tests: resetea la memoización del módulo (jsdom instala/quita
// window.Capacitor entre casos).
export function _resetWarehouseConnectionForTests() {
  warehouse.api = null;
  warehouse.opening = null;
}

export const _WAREHOUSE_KEY_NAME = WAREHOUSE_KEY_NAME;
export const _NATIVE_PLUGIN_TIMEOUT_MS = NATIVE_PLUGIN_TIMEOUT_MS;
export const _DB_KEY_BYTES = DB_KEY_BYTES;
